/**
 * Quest engine (Phase 3). Pure reducer applied from the ONE simulation seam
 * (offline/simulate.ts tickCombat kill branch), so online ticks and offline
 * catch-up credit kills identically.
 *
 * Contract:
 *   - an active kill quest matching the killed monster increments once per
 *     actual kill; nothing else moves;
 *   - on reaching the target the quest completes atomically: XP, reward
 *     items, and unlock flags apply in the same transition, exactly once;
 *   - `reward.items` means ONE of each listed id — the catalogue carries no
 *     quantities (documented rule, enforced by engine.test.ts);
 *   - reward items that fit nowhere go through the same lost-item callback
 *     as combat drops — never silently discarded;
 *   - completed quests are inert: later kills return the same reference.
 */
import { ITEMS, QUESTS_BY_ID } from '../../data';
import type { ItemId, MonsterId, PlayerState, SkillId } from '../state';
import { awardItem, type Containers } from '../economy/containers';
import { levelForXp } from '../skills/xp';

export type QuestEvent =
  | { type: 'quest-complete'; questId: string; questName: string }
  | { type: 'flag-added'; flag: string };

/** Callback for reward items that fit in neither container. */
export type LoseItemFn = (itemId: ItemId, qty: number) => void;

export function hasFlag(state: PlayerState, flag: string): boolean {
  return state.flags.includes(flag);
}

/** Add a one-time flag; same reference when already present. */
export function withFlag(state: PlayerState, flag: string): PlayerState {
  if (state.flags.includes(flag)) return state;
  return { ...state, flags: [...state.flags, flag] };
}

export function questEntry(
  state: PlayerState,
  questId: string,
): PlayerState['quests'][number] | undefined {
  return state.quests.find((q) => q.questId === questId);
}

export function isQuestComplete(state: PlayerState, questId: string): boolean {
  return questEntry(state, questId)?.status === 'complete';
}

export function isQuestActive(state: PlayerState, questId: string): boolean {
  return questEntry(state, questId)?.status === 'active';
}

function dedupeQuestRows(quests: PlayerState['quests']): PlayerState['quests'] {
  const deduped = new Map<string, PlayerState['quests'][number]>();
  for (const entry of quests) {
    const current = deduped.get(entry.questId);
    if (
      !current ||
      (entry.status === 'complete' && current.status !== 'complete') ||
      (entry.status === current.status && entry.progress > current.progress)
    ) {
      deduped.set(entry.questId, entry);
    }
  }
  return [...deduped.values()];
}

/** Credit one monster kill to every matching active quest. */
export function applyQuestKill(
  state: PlayerState,
  monsterId: MonsterId,
  lose: LoseItemFn,
): { state: PlayerState; events: ReadonlyArray<QuestEvent> } {
  const sourceQuests = dedupeQuestRows(state.quests);
  let changed = sourceQuests.length !== state.quests.length;
  const events: QuestEvent[] = [];
  let skills = state.skills;
  let containers: Containers = { bank: state.bank, inventory: state.inventory };
  let flags = state.flags;

  const quests = sourceQuests.map((entry) => {
    if (entry.status !== 'active') return entry;
    const quest = QUESTS_BY_ID.get(entry.questId);
    if (!quest || quest.objective.type !== 'kill' || quest.objective.monsterId !== monsterId) {
      return entry;
    }
    changed = true;
    const progress = Math.min(entry.progress + 1, quest.objective.count);
    if (progress < quest.objective.count) return { ...entry, progress };

    // Completion: everything applies in this one transition, exactly once.
    for (const [skillId, amount] of Object.entries(quest.reward.xp)) {
      if (skillId in skills) {
        const cur = skills[skillId as SkillId];
        const xp = cur.xp + amount;
        skills = { ...skills, [skillId]: { xp, level: levelForXp(xp) } };
      }
    }
    for (const itemId of quest.reward.items) {
      if (!(itemId in ITEMS.items)) continue;
      const awarded = awardItem(containers, itemId as ItemId, 1);
      containers = awarded.containers;
      if (awarded.lost > 0) lose(itemId as ItemId, awarded.lost);
    }
    for (const flag of quest.unlocks) {
      if (!flags.includes(flag)) {
        flags = [...flags, flag];
        events.push({ type: 'flag-added', flag });
      }
    }
    events.push({ type: 'quest-complete', questId: quest.id, questName: quest.name });
    return { ...entry, status: 'complete' as const, progress };
  });

  if (!changed) return { state, events };
  return {
    state: {
      ...state,
      quests,
      skills,
      bank: containers.bank,
      inventory: containers.inventory,
      flags,
    },
    events,
  };
}
